import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { ArrowRight, Wallet, HandCoins, Scale, Plus, Receipt } from 'lucide-react';
import DataTable from '../DataTable';
import ConfirmDialog from '../ConfirmDialog';
import StatCard, { StatCardsRow } from './StatCard';
import BulkPaymentModal from './BulkPaymentModal';
import EditPaymentModal from './EditPaymentModal';
import ClientPaymentsModal from './ClientPaymentsModal';
import { handleApiError } from '../../utils/errorHandler';
import {
  balancesApi,
  PartyBalanceDetail as PartyDetail,
  ClientBalanceRow,
  PendingLeadRow,
  AgentPayment,
  formatCurrency,
  formatDate,
} from '../../services/agentBalancesService';

interface Props {
  partyType: number;
  sourceId: number;
  onBack: () => void;
  onDataChanged: () => void;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: var(--hover-background);
    color: var(--text-primary);
  }
`;

const PartyTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TypeBadge = styled.span<{ $agent: boolean }>`
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  color: #fff;
  background: ${p => (p.$agent ? 'var(--primary-color)' : '#8b5cf6')};
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-sm);
  background: var(--primary-color);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--primary-hover);
  }
`;

const CountsLine = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 20px;

  strong {
    color: var(--text-primary);
  }
`;

const StatusBadge = styled.span<{ $variant: 'signed' | 'approved' | 'pending' }>`
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  margin: 0 2px;
  ${p =>
    p.$variant === 'approved'
      ? 'background: rgba(0,202,114,0.15); color: #0a8f52;'
      : p.$variant === 'signed'
      ? 'background: rgba(0,115,234,0.12); color: var(--primary-color);'
      : 'background: rgba(0,0,0,0.06); color: var(--text-secondary);'}
`;

const Remaining = styled.span<{ $open: boolean }>`
  font-weight: 700;
  direction: ltr;
  color: ${p => (p.$open ? 'var(--error-color)' : '#0a8f52')};
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const SmallButton = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${p => (p.$variant === 'primary' ? 'var(--primary-color)' : 'var(--border-color)')};
  background: ${p => (p.$variant === 'primary' ? 'var(--primary-color)' : 'var(--surface-color)')};
  color: ${p => (p.$variant === 'primary' ? '#fff' : 'var(--text-secondary)')};

  &:hover {
    background: ${p => (p.$variant === 'primary' ? 'var(--primary-hover)' : 'var(--hover-background)')};
    color: ${p => (p.$variant === 'primary' ? '#fff' : 'var(--text-primary)')};
  }
`;

const Muted = styled.span`
  color: var(--text-tertiary);
`;

const SectionTitle = styled.h3`
  margin: 28px 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
`;

const SectionHint = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--text-secondary);
`;

const LeadStatusDot = styled.span<{ $color?: string | null }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 6px;
  background: ${p => p.$color || 'var(--border-color)'};
`;

const PartyBalanceDetail: React.FC<Props> = ({ partyType, sourceId, onBack, onDataChanged }) => {
  const [detail, setDetail] = useState<PartyDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [singleClientId, setSingleClientId] = useState<number | null>(null);

  const [paymentsClient, setPaymentsClient] = useState<ClientBalanceRow | null>(null);
  const [editPayment, setEditPayment] = useState<AgentPayment | null>(null);
  const [deletePayment, setDeletePayment] = useState<AgentPayment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isAgent = partyType === 1;

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      const data = await balancesApi.getPartyDetail(partyType, sourceId);
      setDetail(data);
      // keep the open payments-history modal in sync after edits/deletes
      setPaymentsClient(prev => (prev ? data.clients.find(c => c.clientId === prev.clientId) ?? null : null));
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה בטעינת פירוט הצד'));
    } finally {
      setLoading(false);
    }
  }, [partyType, sourceId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const afterChange = () => {
    loadDetail();
    onDataChanged();
  };

  const openBulk = () => {
    setSingleClientId(null);
    setBulkOpen(true);
  };

  const openSingle = (clientId: number) => {
    setSingleClientId(clientId);
    setBulkOpen(true);
  };

  const confirmDeletePayment = async () => {
    if (!deletePayment) return;
    try {
      setDeleting(true);
      await balancesApi.deletePayment(deletePayment.id);
      toast.success('התשלום נמחק');
      setDeletePayment(null);
      afterChange();
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה במחיקת התשלום'));
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { key: 'clientName', title: 'לקוח', render: (v: string) => <strong>{v}</strong> },
    {
      key: 'status',
      title: 'סטטוס',
      render: (_v: any, r: ClientBalanceRow) => (
        <>
          <StatusBadge $variant="signed">חתם</StatusBadge>
          {r.isAllowanceApproved ? (
            <StatusBadge $variant="approved">אושר</StatusBadge>
          ) : (
            <StatusBadge $variant="pending">ממתין לוועדה</StatusBadge>
          )}
        </>
      ),
    },
    { key: 'signingOwed', title: 'חוב חתימה', render: (v: number) => formatCurrency(v) },
    {
      key: 'approvalOwed',
      title: 'חוב אישור',
      render: (v: number | null | undefined) =>
        v === null || v === undefined ? <Muted>—</Muted> : formatCurrency(v),
    },
    { key: 'totalOwed', title: 'סה"כ חוב', render: (v: number) => formatCurrency(v) },
    { key: 'totalPaid', title: 'שולם', render: (v: number) => formatCurrency(v) },
    {
      key: 'remaining',
      title: 'יתרה',
      render: (v: number) => <Remaining $open={v > 0.001}>{formatCurrency(v)}</Remaining>,
    },
    {
      key: 'actions',
      title: 'פעולות',
      render: (_v: any, r: ClientBalanceRow) => (
        <RowActions>
          <SmallButton $variant="primary" onClick={() => openSingle(r.clientId)}>
            <Plus size={14} /> תשלום
          </SmallButton>
          <SmallButton onClick={() => setPaymentsClient(r)}>
            <Receipt size={14} /> תשלומים ({r.payments.length})
          </SmallButton>
        </RowActions>
      ),
    },
  ];

  const pendingLeadColumns = [
    { key: 'leadName', title: 'שם הליד', render: (v: string) => <strong>{v}</strong> },
    {
      key: 'statusName',
      title: 'סטטוס הליד',
      render: (v: string, r: PendingLeadRow) => (
        <>
          <LeadStatusDot $color={r.statusColor} />
          {v}
        </>
      ),
    },
    { key: 'createdAt', title: 'נוצר בתאריך', render: (v: string) => formatDate(v) },
    {
      key: 'pendingBadge',
      title: 'סטטוס עמלה',
      render: () => <StatusBadge $variant="pending">ממתין לחתימה</StatusBadge>,
    },
  ];

  return (
    <div>
      <Header>
        <HeaderRight>
          <BackButton type="button" onClick={onBack}>
            <ArrowRight size={16} /> חזרה ליתרות
          </BackButton>
          <PartyTitle>
            {isAgent ? <HandCoins size={22} /> : <Scale size={22} />}
            {detail?.sourceName ?? ''}
            <TypeBadge $agent={isAgent}>{isAgent ? 'סוכן' : 'ארגון'}</TypeBadge>
          </PartyTitle>
        </HeaderRight>
        <PrimaryButton type="button" onClick={openBulk} disabled={!detail}>
          <Wallet size={16} /> צור תשלום מרוכז
        </PrimaryButton>
      </Header>

      {detail && (
        <>
          <StatCardsRow>
            <StatCard title="סה&quot;כ חוב" value={formatCurrency(detail.totalOwed)} color="var(--primary-color)" icon={<HandCoins size={20} />} />
            <StatCard title="שולם" value={formatCurrency(detail.totalPaid)} color="#0a8f52" icon={<Wallet size={20} />} />
            <StatCard title="יתרה לתשלום" value={formatCurrency(detail.remaining)} color={detail.remaining > 0.001 ? 'var(--error-color)' : '#0a8f52'} icon={<Scale size={20} />} />
          </StatCardsRow>

          <CountsLine>
            <strong>{detail.signedClientCount}</strong> לקוחות חתמו דרך צד זה · מתוכם <strong>{detail.approvedClientCount}</strong> אושרו בוועדה
            {detail.pendingLeadCount > 0 && (
              <> · <strong>{detail.pendingLeadCount}</strong> לידים ממתינים לחתימה</>
            )}
          </CountsLine>
        </>
      )}

      <DataTable<ClientBalanceRow>
        data={detail?.clients ?? []}
        columns={columns}
        loading={loading}
      />

      {detail && detail.pendingLeads.length > 0 && (
        <>
          <SectionTitle>לידים ממתינים לחתימה ({detail.pendingLeads.length})</SectionTitle>
          <SectionHint>לידים אלו טרם חתמו — אין בגינם חוב, והם מוצגים לצפי בלבד.</SectionHint>
          <DataTable<PendingLeadRow> data={detail.pendingLeads} columns={pendingLeadColumns} />
        </>
      )}

      {detail && (
        <BulkPaymentModal
          isOpen={bulkOpen}
          onClose={() => setBulkOpen(false)}
          party={detail}
          singleClientId={singleClientId}
          onSaved={afterChange}
        />
      )}

      {paymentsClient && (
        <ClientPaymentsModal
          isOpen={!!paymentsClient}
          onClose={() => setPaymentsClient(null)}
          clientName={paymentsClient.clientName}
          payments={paymentsClient.payments}
          onEdit={p => setEditPayment(p)}
          onDelete={p => setDeletePayment(p)}
        />
      )}

      <EditPaymentModal
        isOpen={!!editPayment}
        onClose={() => setEditPayment(null)}
        payment={editPayment}
        onSaved={afterChange}
      />

      <ConfirmDialog
        isOpen={!!deletePayment}
        onClose={() => setDeletePayment(null)}
        onConfirm={confirmDeletePayment}
        title="מחיקת תשלום"
        message={deletePayment ? `למחוק תשלום על סך ${formatCurrency(deletePayment.amount)}? פעולה זו אינה הפיכה.` : ''}
        confirmText="מחק"
        type="danger"
        loading={deleting}
      />
    </div>
  );
};

export default PartyBalanceDetail;
